import { Card, Tooltip } from "antd"
import { DollarOutlined, RiseOutlined, PercentageOutlined, AimOutlined } from "@ant-design/icons"
import styles from "./KpiSummaryCards.module.scss"

interface KpiSummaryCardsProps {
  totalSpend: number
  totalSales: number
  activeCampaigns: number
  totalCampaigns?: number
  loading?: boolean
}

const formatCompact = (n: number, isCurrency = false): string => {
  const sign = isCurrency ? "$" : ""
  if (!Number.isFinite(n) || n === 0) return `${sign}0`
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${sign}${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${(n / 1_000).toFixed(1)}k`
  return `${sign}${Math.round(n).toLocaleString()}`
}

const getAcosColor = (acos: number): string => {
  if (acos <= 0 || !Number.isFinite(acos)) return styles.valueMuted
  if (acos < 20) return styles.valueSuccess
  if (acos < 35) return styles.valueWarning
  return styles.valueDanger
}

const KpiSummaryCards: React.FC<KpiSummaryCardsProps> = ({
  totalSpend,
  totalSales,
  activeCampaigns,
  totalCampaigns,
  loading = false,
}) => {
  const acos = totalSales > 0 ? Number(((totalSpend / totalSales) * 100).toFixed(1)) : 0

  const showCounter = typeof totalCampaigns === "number"
  const activeLabel = showCounter
    ? `${activeCampaigns} / ${totalCampaigns}`
    : String(activeCampaigns)

  return (
    <div className={styles.grid}>
      <Card className={styles.card} loading={loading} variant="borderless">
        <div className={styles.row}>
          <div className={`${styles.iconChip} ${styles.iconPrimary}`}>
            <DollarOutlined />
          </div>
          <div className={styles.text}>
            <p className={styles.label}>Total Spend</p>
            <p className={styles.value}>{formatCompact(totalSpend, true)}</p>
          </div>
        </div>
      </Card>

      <Card className={styles.card} loading={loading} variant="borderless">
        <div className={styles.row}>
          <div className={`${styles.iconChip} ${styles.iconSuccess}`}>
            <RiseOutlined />
          </div>
          <div className={styles.text}>
            <p className={styles.label}>Total Sales</p>
            <p className={styles.value}>{formatCompact(totalSales, true)}</p>
          </div>
        </div>
      </Card>

      <Card className={styles.card} loading={loading} variant="borderless">
        <Tooltip
          title={totalSales > 0 ? "Ad spend as % of ad sales" : "No sales data for this period"}
          mouseEnterDelay={0.4}
        >
          <div className={styles.row}>
            <div className={`${styles.iconChip} ${styles.iconWarning}`}>
              <PercentageOutlined />
            </div>
            <div className={styles.text}>
              <p className={styles.label}>ACOS</p>
              <p className={`${styles.value} ${getAcosColor(acos)}`}>
                {acos > 0 ? `${acos}%` : "—"}
              </p>
            </div>
          </div>
        </Tooltip>
      </Card>

      <Card className={styles.card} loading={loading} variant="borderless">
        <div className={styles.row}>
          <div className={`${styles.iconChip} ${styles.iconInfo}`}>
            <AimOutlined />
          </div>
          <div className={styles.text}>
            <p className={styles.label}>Active Campaigns</p>
            <p className={styles.value}>{activeLabel}</p>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default KpiSummaryCards
